import { useEffect, useCallback } from 'react';
import 'driver.js/dist/driver.css';
import { useAuth } from '@/hooks/useAuth';
import { useMembership } from '@/hooks/useMembership';
import { hasTourCompleted, launchTour } from './tourUtils';

interface GuidedTourProps {
  /** Force start the tour even if already completed */
  forceStart?: boolean;
  /** Callback when tour completes */
  onComplete?: () => void;
}

/**
 * GuidedTour component that provides an interactive walkthrough
 * of the application for new users.
 */
export function GuidedTour({ forceStart = false, onComplete }: GuidedTourProps) {
  const { user } = useAuth();
  const { membership } = useMembership();

  const isTechnician = membership?.role === 'technician';

  const startTour = useCallback(() => {
    launchTour({ isTechnician, onComplete });
  }, [isTechnician, onComplete]);

  useEffect(() => {
    if (!user) return;

    // Check if tour should start
    const tourCompleted = hasTourCompleted();

    if (forceStart || !tourCompleted) {
      // Delay to ensure page is fully rendered
      const timer = setTimeout(startTour, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, forceStart, startTour]);

  // This component doesn't render anything visible
  return null;
}
